import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import MovieModal from '../components/MovieModal';
import { IMG_W500, IMG_BASE } from '../utils/tmdb';
import { useAuth } from '../context/AuthContext';
import { Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyList = () => {
  const { currentProfile } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mylist`);
      setItems(data.items || []);
    } catch (e) {
      console.error('[MyList] Failed to load list:', e);
      setItems([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (currentProfile) refresh(); }, [currentProfile, refresh]);

  const removeItem = async (it, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/mylist/${it.media_type}/${it.item_id}`);
      setItems((prev) => prev.filter((x) => !(x.item_id === it.item_id && x.media_type === it.media_type)));
    } catch (err) {
      console.error('[MyList] Failed to remove item:', err);
    }
  };

  const openItem = (it) => {
    setSelected({
      id: it.item_id,
      media_type: it.media_type,
      title: it.title,
      backdrop_path: it.backdrop_path,
      poster_path: it.poster_path,
      overview: it.overview,
      first_air_date: it.media_type === 'tv' ? '2020-01-01' : undefined,
    });
  };

  return (
    <div className="bg-[#141414] min-h-screen text-white">
      <Navbar />
      <div className="pt-28 px-4 md:px-14 pb-16 max-w-7xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold mb-8">My List</h1>
        {loading ? (
          <p className="text-white/60">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-white/70 text-lg">Your list is empty.</p>
            <p className="text-white/50 text-sm mt-2">Tap the + button on any title to save it here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {items.map((it) => {
              const src = it.poster_path ? `${IMG_W500}${it.poster_path}` : (it.backdrop_path ? `${IMG_BASE}${it.backdrop_path}` : '');
              return (
                <div
                  key={`${it.media_type}-${it.item_id}`}
                  onClick={() => openItem(it)}
                  className="relative overflow-hidden rounded-md group cursor-pointer"
                >
                  {src ? (
                    <img src={src} alt={it.title} className="w-full aspect-[2/3] object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                  ) : (
                    <div className="w-full aspect-[2/3] bg-white/10" />
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2 pt-6">
                    <div className="text-sm font-medium truncate">{it.title}</div>
                    <div className="text-[11px] text-white/60 uppercase">{it.media_type}</div>
                  </div>
                  <button
                    onClick={(e) => removeItem(it, e)}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-black/70 hover:bg-black text-white p-1.5 rounded transition-opacity"
                    aria-label="remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MovieModal
        item={selected}
        onClose={() => setSelected(null)}
        onListChanged={refresh}
      />
    </div>
  );
};

export default MyList;
